/**
 * Where a customer can go from an instance page.
 *
 * ⚠ EXTRACTED SO THE BILLING LINK CAN BE TESTED. On 14 September 2026 the whole
 * product contained exactly ONE link to the billing page: inside the grid's
 * preview panel, rendered only when a write was refused for lack of a
 * subscription. So the page was reachable only by people who had not paid, and
 * became unreachable the instant they did — no way to see the subscription,
 * replace an expiring card, get an invoice, or CANCEL, short of typing a URL
 * they had never been shown.
 *
 * That is the same asymmetry recorded against instance removal: something a
 * customer can do in two clicks should not become something they have to work
 * out how to undo. Cancellation especially — a subscription that is easy to
 * start and hard to stop is the kind of thing that gets written about, and the
 * billing page is also where we tell people their data will be deleted.
 */
export interface TenantLink {
  to: string;
  label: string;
  /** Rendered as the prominent action. */
  primary?: boolean;
}

export function tenantLinks(
  tenantId: string,
  connectionStatus: string,
  entities: Array<{ name: string; label: string }>
): TenantLink[] {
  // ⚠ Billing does NOT depend on the connection being healthy, deliberately.
  // A customer whose instance is broken may want the billing page MORE than
  // one whose instance works — to stop paying for something that is not
  // serving them. Gating it on a working ERP connection would mean the worse
  // the experience, the harder it is to leave.
  const links: TenantLink[] = [{ to: `/tenants/${tenantId}/billing`, label: 'Billing' }];

  // The data screens stay gated: there is genuinely nothing to show until the
  // instance is reachable.
  if (connectionStatus === 'connected') {
    links.push({ to: `/tenants/${tenantId}/jobs`, label: 'Job history' });
    for (const entity of entities) {
      links.push({
        to: `/tenants/${tenantId}/entities/${entity.name}`,
        label: entity.label,
        primary: true,
      });
    }
  }

  return links;
}
